import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme';

interface CardProps {
    children: React.ReactNode;
    padding?: 'sm' | 'md' | 'lg' | 'xl';
    shadow?: 'sm' | 'md' | 'lg' | 'xl';
    hover?: boolean;
    className?: string;
}

const StyledCard = styled.div<{
  padding?: 'sm' | 'md' | 'lg' | 'xl';
  shadow?: 'sm' | 'md' | 'lg' | 'xl';
  hover?: boolean;
}>`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.light.border.light};
  transition: all 0.3s ease-in-out;
  backdrop-filter: blur(10px);

  ${({ padding = 'md' }) => {
        switch (padding) {
            case 'sm':
                return css`
          padding: ${theme.spacing[4]};
        `;
            case 'lg':
                return css`
          padding: ${theme.spacing[8]};
        `;
            case 'xl':
                return css`
          padding: ${theme.spacing[10]};
        `;
            default:
                return css`
          padding: ${theme.spacing[6]};
        `;
        }
    }}

  ${({ shadow = 'sm' }) => {
        switch (shadow) {
            case 'md':
                return css`
          box-shadow: ${theme.shadows.md};
        `;
            case 'lg':
                return css`
          box-shadow: ${theme.shadows.lg};
        `;
            case 'xl':
                return css`
          box-shadow: ${theme.shadows.xl};
        `;
            default:
                return css`
          box-shadow: ${theme.shadows.sm};
        `;
        }
    }}

  ${({ hover }) =>
        hover &&
        css`
      cursor: pointer;

      &:hover {
        transform: translateY(-2px);
        box-shadow: ${theme.shadows.lg};
        border-color: ${theme.colors.primary[200]};
      }
    `}
`;

export const Card: React.FC<CardProps> = ({ children, ...props }) => {
    return <StyledCard {...props}>{children}</StyledCard>;
};

// Card sub-components
export const CardHeader = styled.div`
  margin-bottom: ${theme.spacing[4]};
  padding-bottom: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
`;

export const CardTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
  letter-spacing: -0.02em;
`;

export const CardDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin: ${theme.spacing[1]} 0 0 0;
  line-height: 1.5;
`;

export const CardBody = styled.div`
  flex: 1;
`;

export const CardFooter = styled.div`
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.light.border.light};
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing[3]};
`;
